<template>
  <DefaultField :field="field" :errors="errors" :show-help-text="showHelpText" :full-width-content="true">
    <template #field>
      <div class="sq">
        <div class="sq-list">
          <div v-for="(q, i) in questions" :key="q.key" class="sq-card">
            <div class="sq-card-head">
              <span class="sq-index">{{ i + 1 }}</span>

              <select v-model="q.type" class="form-control form-input form-input-bordered sq-type">
                <option v-for="t in field.types" :key="t.value" :value="t.value">{{ t.label }}</option>
              </select>

              <div class="sq-head-actions">
                <button type="button" class="sq-icon" :disabled="i === 0" @click="move(i, -1)" title="↑">↑</button>
                <button type="button" class="sq-icon" :disabled="i === questions.length - 1" @click="move(i, 1)" title="↓">↓</button>
                <button type="button" class="sq-icon sq-danger" @click="remove(i)" title="×">×</button>
              </div>
            </div>

            <textarea
              v-model="q.prompt"
              rows="2"
              class="form-control form-input form-input-bordered w-full"
              :placeholder="placeholders.prompt"
            />

            <div v-if="hasOptions(q)" class="sq-options">
              <div v-for="(opt, oi) in q.options" :key="oi" class="sq-option">
                <span class="sq-bullet">{{ oi + 1 }}</span>
                <input
                  v-model="q.options[oi]"
                  type="text"
                  class="form-control form-input form-input-bordered w-full"
                  :placeholder="placeholders.option"
                />
                <button type="button" class="sq-icon sq-danger" @click="q.options.splice(oi, 1)">×</button>
              </div>
              <button type="button" class="sq-add-option" @click="q.options.push('')">
                + {{ placeholders.addOption }}
              </button>
            </div>

            <p v-else class="sq-hint">{{ hintFor(q) }}</p>

            <label class="sq-required">
              <input type="checkbox" v-model="q.required" class="checkbox" />
              <span>{{ placeholders.required }}</span>
            </label>
          </div>
        </div>

        <button type="button" class="sq-add" @click="add">+ {{ placeholders.addQuestion }}</button>

        <p v-if="!questions.length" class="sq-empty">{{ placeholders.empty }}</p>
      </div>
    </template>
  </DefaultField>
</template>

<script>
import { FormField, HandlesValidationErrors } from '@/mixins'

export default {
  mixins: [FormField, HandlesValidationErrors],

  props: ['resourceName', 'resourceId', 'field'],

  data: () => ({
    questions: [],
    seq: 0,
  }),

  computed: {
    /**
     * Persian labels kept in one place so the template stays readable.
     */
    placeholders() {
      return {
        prompt: 'متن سؤال…',
        option: 'متن دکمه…',
        addOption: 'افزودن گزینه',
        addQuestion: 'افزودن سؤال',
        required: 'پاسخ به این سؤال اجباری است',
        empty: 'هنوز سؤالی اضافه نشده است.',
      }
    },
  },

  methods: {
    /**
     * Load whatever the survey already has.
     */
    setInitialValue() {
      const value = this.field.value || []
      this.questions = (Array.isArray(value) ? value : []).map(q => ({
        key: this.seq++,
        id: q.id || 0,
        type: q.type || this.field.defaultType,
        prompt: q.prompt || '',
        required: q.required !== false,
        options: Array.isArray(q.options) ? [...q.options] : [],
      }))
    },

    /**
     * Send the list as JSON; the field syncs it after the survey is saved.
     */
    fill(formData) {
      formData.append(this.field.attribute, JSON.stringify(this.questions))
    },

    /**
     * Whether this type is answered by picking one of the admin's options.
     */
    hasOptions(question) {
      return question.type === this.field.choiceType
    },

    /**
     * What the subscriber will see for a type that builds its own buttons.
     */
    hintFor(question) {
      const hints = {
        boolean: 'دو دکمه: بله / خیر',
        rating: 'پنج دکمه: ۱ تا ۵ ستاره',
        text: 'کاربر پاسخ را می‌نویسد',
      }
      return hints[question.type] || ''
    },

    add() {
      this.questions.push({
        key: this.seq++,
        id: 0,
        type: this.field.defaultType,
        prompt: '',
        required: true,
        options: ['', ''],
      })
    },

    remove(index) {
      this.questions.splice(index, 1)
    },

    move(index, direction) {
      const target = index + direction
      if (target < 0 || target >= this.questions.length) return
      const [row] = this.questions.splice(index, 1)
      this.questions.splice(target, 0, row)
    },
  },
}
</script>

<style scoped>
.sq-list { display: flex; flex-direction: column; gap: 0.75rem; }
.sq { display: flex; flex-direction: column; gap: 0.75rem; }
.sq-card {
  border: 1px solid rgba(var(--colors-gray-300), 1);
  border-radius: 0.5rem;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: rgba(var(--colors-gray-50), 0.6);
}
.sq-card-head { display: flex; align-items: center; gap: 0.5rem; }
.sq-index {
  width: 1.75rem; height: 1.75rem; flex: none;
  display: flex; align-items: center; justify-content: center;
  border-radius: 999px; font-weight: 700; font-size: 0.8rem;
  background: rgba(var(--colors-primary-500), 0.12);
  color: rgba(var(--colors-primary-600), 1);
}
.sq-type { width: auto; min-width: 11rem; }
.sq-head-actions { margin-inline-start: auto; display: flex; gap: 0.25rem; }
.sq-icon {
  width: 1.9rem; height: 1.9rem; border-radius: 0.375rem;
  border: 1px solid rgba(var(--colors-gray-300), 1);
  line-height: 1; font-size: 1rem;
}
.sq-icon:disabled { opacity: 0.35; }
.sq-danger { color: rgba(var(--colors-red-500), 1); }
.sq-options { display: flex; flex-direction: column; gap: 0.375rem; padding-inline-start: 2.25rem; }
.sq-option { display: flex; align-items: center; gap: 0.5rem; }
.sq-bullet { font-size: 0.75rem; opacity: 0.6; width: 1rem; text-align: center; }
.sq-add, .sq-add-option {
  align-self: flex-start; font-size: 0.85rem; font-weight: 600;
  color: rgba(var(--colors-primary-600), 1);
}
.sq-add { padding: 0.4rem 0; }
.sq-hint { font-size: 0.8rem; opacity: 0.65; padding-inline-start: 2.25rem; }
.sq-required { display: flex; align-items: center; gap: 0.5rem; font-size: 0.85rem; }
.sq-empty { font-size: 0.85rem; opacity: 0.6; }
</style>
